import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Lock, Delete, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { fixedSlots } from "@/lib/listIdentity";

const PIN_LENGTH = 4;

export function AdminPinGate() {
  const { verifyPin } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const press = async (digit) => {
    if (busy || pin.length >= PIN_LENGTH) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === PIN_LENGTH) {
      setBusy(true);
      try {
        await verifyPin(next);
      } catch (_) {
        await api.post("/auth/logout").catch(() => {});
        // Wrong PIN: nuke the session and restart the whole flow.
        setError(true);
        localStorage.removeItem("cc_token");
        sessionStorage.clear();
        setTimeout(() => { window.location.href = "/login"; }, 700);
      }
    }
  };

  const back = () => { if (!busy) { setPin((p) => p.slice(0, -1)); } };

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];

  return (
    <div
      data-testid="admin-pin-gate"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[hsl(220_30%_4%)]/95 backdrop-blur-md"
    >
      <div className="ambient-scanlines absolute inset-0 pointer-events-none" />
      <div className="hud-pill relative w-full max-w-xs rounded-2xl p-7">
        <div className="flex items-center gap-2 justify-center mb-1">
          <Lock className="h-4 w-4 text-cyan-300" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">Secure Access</div>
        </div>
        <h2 className="text-center font-display text-2xl text-white tracking-[0.06em] mt-1">ADMIN PIN</h2>
        <p className="text-center text-white/45 text-xs mt-1">Enter your 4-digit PIN to continue.</p>

        <div className="flex justify-center gap-3 my-6" data-testid="admin-pin-dots">
          {fixedSlots(PIN_LENGTH).map(({ id, index: i }) => (
            <div
              key={id}
              className={`h-3.5 w-3.5 rounded-full border transition-colors ${
                error
                  ? "bg-rose-500 border-rose-400"
                  : i < pin.length
                  ? "bg-cyan-400 border-cyan-300"
                  : "bg-transparent border-white/25"
              }`}
            />
          ))}
        </div>

        {error && (
          <div className="flex items-center justify-center gap-1.5 text-rose-400 text-xs mb-3" data-testid="admin-pin-error">
            <ShieldAlert className="h-3.5 w-3.5" /> Access denied — restarting…
          </div>
        )}

        <div className="grid grid-cols-3 gap-2.5">
          {keys.map((k) => {
            if (k === "") return <div key="keypad-spacer" />;
            if (k === "del") {
              return (
                <button
                  key={k}
                  onClick={back}
                  data-testid="admin-pin-delete"
                  disabled={busy}
                  className="h-14 rounded-xl bg-white/5 border border-white/10 text-white/70 flex items-center justify-center hover:bg-white/10 active:scale-95 transition-transform disabled:opacity-40"
                >
                  <Delete className="h-5 w-5" />
                </button>
              );
            }
            return (
              <button
                key={k}
                onClick={() => press(k)}
                data-testid={`admin-pin-key-${k}`}
                disabled={busy}
                className="h-14 rounded-xl bg-[hsl(220_18%_12%)] border border-white/10 text-white text-xl font-display tracking-wide hover:border-cyan-400/50 hover:text-cyan-200 active:scale-95 transition-transform disabled:opacity-40"
              >
                {k}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
